import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { SectionTitle } from "@/components/dashboard/widgets";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Search, RefreshCw, Download, CloudUpload, Loader2, CheckCircle2, XCircle, Trash2 } from "lucide-react";
import { RowBin, ClearArea } from "@/components/admin/Purge";
import { toast } from "sonner";

function S3Card() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ access_key_id: "", secret_access_key: "", bucket: "", region: "", prefix: "" });
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/audit/s3");
      setData(r.data);
      if (r.data.config) setForm((f) => ({ ...f, bucket: r.data.config.bucket, region: r.data.config.region, prefix: r.data.config.prefix || "" }));
    } catch { setData({ config: null, exports: [] }); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy("save");
    try {
      await api.put("/admin/audit/s3", { ...form, enabled: true });
      toast.success("S3 connected — daily audit replication is now ON (02:05 UTC)");
      setForm((f) => ({ ...f, access_key_id: "", secret_access_key: "" }));
      setShowForm(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "AWS rejected the details"); }
    finally { setBusy(null); }
  };

  const exportNow = async () => {
    setBusy("export");
    try {
      const r = await api.post("/admin/audit/s3/export");
      toast.success(`Exported ${r.data.rows} rows → s3://${data.config.bucket}/${r.data.key}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Export failed"); }
    finally { setBusy(null); }
  };

  const disconnect = async () => {
    setBusy("disconnect");
    try { await api.delete("/admin/audit/s3"); toast.success("S3 replication disconnected"); load(); }
    catch { toast.error("Disconnect failed"); }
    finally { setBusy(null); }
  };

  if (!data) return null;
  const cfg = data.config;

  return (
    <Card className="rounded-xl border-slate-200 shadow-soft bg-white" data-testid="audit-s3-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="inline-grid place-items-center size-8 rounded-lg bg-[#0F172A] text-white"><CloudUpload className="size-4" strokeWidth={1.6} /></span>
            <div>
              <div className="text-[13.5px] font-semibold text-slate-900">S3 replication</div>
              <div className="text-[11.5px] text-slate-500">Daily automated export of the full audit trail to your own AWS bucket, plus on-demand exports.</div>
            </div>
          </div>
          {cfg ? (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 px-3 h-7 text-[11.5px] font-semibold" data-testid="audit-s3-status">
                <ShieldCheck className="size-3" /> s3://{cfg.bucket} · {cfg.region} · {cfg.access_key_hint}
              </span>
              <Button onClick={exportNow} variant="outline" size="sm" className="rounded-full" disabled={busy === "export"} data-testid="audit-s3-export-now">
                {busy === "export" ? <Loader2 className="size-3.5 animate-spin" /> : <CloudUpload className="size-3.5" />} Export now
              </Button>
              <Button onClick={() => setShowForm((v) => !v)} variant="outline" size="sm" className="rounded-full" data-testid="audit-s3-update-keys">Update keys</Button>
              <Button onClick={disconnect} variant="ghost" size="sm" className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50" disabled={busy === "disconnect"} data-testid="audit-s3-disconnect">
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          ) : (
            <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 text-amber-700 px-3 h-7 text-[11.5px] font-semibold" data-testid="audit-s3-not-connected">Not connected — paste AWS keys below</span>
          )}
        </div>

        {(!cfg || showForm) && (
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5 block">Access Key ID</label>
              <Input value={form.access_key_id} onChange={(e) => setForm({ ...form, access_key_id: e.target.value })} placeholder="AKIA…" data-testid="audit-s3-access-key" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5 block">Secret Access Key</label>
              <Input type="password" value={form.secret_access_key} onChange={(e) => setForm({ ...form, secret_access_key: e.target.value })} placeholder={cfg ? "Saved — paste to replace" : "Paste secret…"} data-testid="audit-s3-secret-key" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5 block">Bucket</label>
              <Input value={form.bucket} onChange={(e) => setForm({ ...form, bucket: e.target.value })} placeholder="my-audit-bucket" data-testid="audit-s3-bucket" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5 block">Region</label>
              <Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="eu-west-2" data-testid="audit-s3-region" />
            </div>
            <Button onClick={save} className="rounded-full h-10 bg-[#0055FF] hover:bg-[#0044CC]" disabled={busy === "save" || !form.access_key_id.trim() || !form.bucket.trim() || !form.region.trim()} data-testid="audit-s3-save">
              {busy === "save" ? <Loader2 className="size-4 animate-spin" /> : "Connect & verify"}
            </Button>
          </div>
        )}

        {(data.exports || []).length > 0 && (
          <div className="mt-4 border-t border-slate-100 pt-3 space-y-1.5" data-testid="audit-s3-exports">
            <div className="text-[10.5px] uppercase tracking-widest text-slate-400 font-semibold">Recent exports</div>
            {data.exports.slice(0, 5).map((e) => (
              <div key={e.id} className="flex items-center gap-2 text-[12px]">
                {e.ok ? <CheckCircle2 className="size-3.5 text-emerald-600 shrink-0" /> : <XCircle className="size-3.5 text-red-500 shrink-0" />}
                <span className="font-mono text-[11px] text-slate-500">{new Date(e.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                <span className="text-slate-600 truncate">{e.ok ? `${e.rows} rows → ${e.key}` : (e.error || "failed")}</span>
                <span className="text-[10px] text-slate-400 uppercase ml-auto shrink-0">{e.source}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const METHOD_BG = {
  POST:   "bg-emerald-50 text-emerald-700 border-emerald-200",
  PATCH:  "bg-amber-50 text-amber-700 border-amber-200",
  PUT:    "bg-amber-50 text-amber-700 border-amber-200",
  DELETE: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function AdminAudit() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState({ actor_email: "", path_prefix: "", from_iso: "", to_iso: "" });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.actor_email.trim()) params.actor_email = filter.actor_email.trim();
      if (filter.path_prefix.trim()) params.path_prefix = filter.path_prefix.trim();
      const { data } = await api.get("/admin/audit", { params: { limit: 200, ...params } });
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filter.actor_email.trim()) params.set("actor_email", filter.actor_email.trim());
      if (filter.path_prefix.trim()) params.set("path_prefix", filter.path_prefix.trim());
      if (filter.from_iso) params.set("from_iso", new Date(filter.from_iso).toISOString());
      if (filter.to_iso)   params.set("to_iso",   new Date(filter.to_iso).toISOString());
      const r = await api.get(`/admin/audit/export.csv?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0,10);
      a.download = `connexis-audit-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => { load(); }, []); // initial load

  return (
    <div className="space-y-6">
      <SectionTitle
        title="Audit log"
        subtitle="Every administrative write action — actor, IP, method, path, status. Auto-recorded."
        actions={
          <div className="flex items-center gap-2">
            <ClearArea area="telemetry" onDone={load} label="Clear logs" />
            <Button onClick={exportCsv} variant="outline" size="sm" className="rounded-full" disabled={exporting} data-testid="audit-export-csv">
              {exporting ? <Download className="size-3.5 animate-pulse" /> : <Download className="size-3.5" />} Export CSV
            </Button>
            <Button onClick={load} variant="outline" size="sm" className="rounded-full" disabled={loading} data-testid="audit-refresh">
              {loading ? <RefreshCw className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />} Refresh
            </Button>
          </div>
        }
      />

      <S3Card />

      <Card className="rounded-xl border-slate-200 shadow-soft bg-white">
        <CardContent className="p-5">
          <div className="grid sm:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-3 items-end">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5 block">Actor email</label>
              <div className="relative">
                <Search className="size-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  value={filter.actor_email}
                  onChange={(e) => setFilter({ ...filter, actor_email: e.target.value })}
                  placeholder="admin@connex.is"
                  className="pl-9"
                  data-testid="audit-filter-email"
                />
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5 block">Path prefix</label>
              <Input
                value={filter.path_prefix}
                onChange={(e) => setFilter({ ...filter, path_prefix: e.target.value })}
                placeholder="/api/admin/buyers"
                data-testid="audit-filter-path"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5 block">From (CSV)</label>
              <Input type="date" value={filter.from_iso} onChange={(e) => setFilter({ ...filter, from_iso: e.target.value })} data-testid="audit-filter-from"/>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5 block">To (CSV)</label>
              <Input type="date" value={filter.to_iso} onChange={(e) => setFilter({ ...filter, to_iso: e.target.value })} data-testid="audit-filter-to"/>
            </div>
            <Button onClick={load} className="rounded-full h-10 bg-[#0055FF] hover:bg-[#0044CC]" data-testid="audit-apply">Apply</Button>
          </div>
        </CardContent>
      </Card>

      <div className="rounded-xl border border-slate-200 bg-white shadow-soft overflow-hidden" data-testid="audit-log-table">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-3">
          <span className="inline-grid place-items-center size-8 rounded-lg bg-[#0F172A] text-white">
            <ShieldCheck className="size-4" strokeWidth={1.6} />
          </span>
          <div className="flex-1">
            <div className="text-[13.5px] font-medium text-slate-900">{rows.length} event{rows.length === 1 ? "" : "s"}</div>
            <div className="text-[11.5px] text-slate-500">Sorted newest first. Limit 200.</div>
          </div>
        </div>
        {rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">No audit events match the current filter.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map((r) => (
              <div key={r.id} className="px-5 py-3 grid grid-cols-[auto_auto_1fr_auto_auto_auto] gap-3 items-center text-sm" data-testid={`audit-row-${r.id}`}>
                <span className="font-mono text-[11px] text-slate-500 tabular-nums w-[120px]">
                  {new Date(r.ts).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                </span>
                <span className={`inline-flex items-center text-[10px] font-mono font-semibold uppercase tracking-wider rounded border px-2 py-0.5 ${METHOD_BG[r.method] || "bg-slate-50 text-slate-700 border-slate-200"}`}>
                  {r.method}
                </span>
                <span className="font-mono text-[12px] text-slate-700 truncate">{r.path}{r.query ? `?${r.query}` : ""}</span>
                <span className="text-[11px] font-mono text-slate-500">
                  {r.actor_email || <span className="text-slate-400">anon</span>}
                  {r.ip && <span className="text-slate-400"> · {r.ip}</span>}
                </span>
                <span className={`text-[11px] font-mono tabular-nums shrink-0 ${r.status_code >= 400 ? "text-rose-600" : "text-emerald-600"}`}>
                  {r.status_code}
                </span>
                <RowBin area="telemetry" id={r.id} name={`${r.method} ${r.path}`} onDone={load} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
